// `hookmyapp sandbox listen` — Commander subcommand wiring the 11-step flow
// from 107-CONTEXT.md §CLI Flow. Composes the Plan 09a modules (binary,
// proxy-server, summarizer, version-check) with Plan 09b's picker + lifecycle.
//
// Exit code contract (CONTEXT.md §CLI Flow Exit codes):
//   0  clean
//   1  not authenticated
//   2  no active sessions / --phone|--session mismatch
//   3  tunnel provisioning failed (backend /tunnel/start or /configure)
//   4  cloudflared download/checksum failed
//   5  backend unreachable
//   7  cloudflared child exited unexpectedly while listen was running

import type { Command } from 'commander';
import { apiClient } from '../../api/client.js';
import { parseSandboxSessions } from '../../api/sandbox-session.js';
import { CliError, AuthError, ConflictError, ValidationError } from '../../output/error.js';
import { resolveEnv } from '../../config/env-profiles.js';
import { addExamples } from '../../output/help.js';
import { cliCommandPrefix } from '../../output/cli-self.js';
import { readCredentials } from '../../auth/store.js';
import { getDefaultWorkspaceId } from '../_helpers.js';
import { isValidPublicId } from '../../lib/publicId.js';
import { ensureCloudflaredBinary } from './binary.js';
import { startProxyServer, type LogLine } from './proxy-server.js';
import { pickSession, type Session } from './picker.js';
import { sessionIdentifier } from '../sandbox/helpers.js';
import {
  spawnCloudflared,
  startHeartbeat,
  startPosthogLiveness,
  gracefulShutdown,
} from './lifecycle.js';
import { checkForNewerCli } from './version-check.js';
import { emit, getCliVersion } from '../../observability/posthog.js';
import { parsePortArg } from '../../lib/validate.js';

export interface ListenOpts {
  port: number;
  path: string;
  phone?: string;
  username?: string;
  session?: string;
  verbose: boolean;
  json: boolean;
  reinstallTunnelBinary: boolean;
}

export interface TunnelStartResponse {
  cloudflareTunnelToken: string;
  hostname: string;
  webhookPath?: string;
}

/**
 * Execute the sandbox-listen flow against an already-resolved session.
 *
 * The wizard (src/auth/login.ts runSandboxFlow) has already picked or created
 * the session, so this entry point skips Steps 1-5 (auth gate, version check,
 * session picker) and drives Steps 6-11 directly. Imported by the wizard via
 * a direct function call — never subprocess spawn.
 */
export async function runSandboxListenFlow(
  session: Session,
  opts: Partial<ListenOpts> = {},
): Promise<void> {
  const human = !opts.json;
  const listenOpts: ListenOpts = {
    port: opts.port ?? 3000,
    path: opts.path ?? '/webhook',
    phone: opts.phone,
    session: opts.session,
    verbose: opts.verbose ?? false,
    json: opts.json ?? false,
    reinstallTunnelBinary: opts.reinstallTunnelBinary ?? false,
  };

  // Step 3 — ensure cloudflared binary (exit 4 on download/checksum failure).
  let binaryPath: string;
  try {
    binaryPath = await ensureCloudflaredBinary({
      force: listenOpts.reinstallTunnelBinary,
    });
  } catch (err) {
    if (err instanceof CliError) {
      console.error(`cloudflared: ${err.userMessage}`);
      process.exit(4);
    }
    throw err;
  }

  // Step 6 — start tunnel. The backend already returns enforcement-matrix
  // copy verbatim (phase-108 plan 03) for 409 LISTENER_ACTIVE_SAME /
  // LISTENER_ACTIVE / PHONE_TAKEN_ANOTHER — let those propagate as
  // ConflictError (exit 6) so users see the remediation text. Non-conflict
  // provisioning failures (5xx, timeouts) still map to exit 3.
  //
  // Test-only escape hatch: HOOKMYAPP_E2E_FAKE_TUNNEL=1 short-circuits the
  // three tunnel-lifecycle backend calls (start/configure/stop) and returns
  // a synthetic TunnelStartResponse. Used by
  // test-integration/specs/sandbox-listen.spec.ts so the lifecycle test
  // doesn't mint a real Cloudflare tunnel per case (would create 3 real CF
  // resources per CI run + add ~2-4s to teardown via real CF DELETE calls).
  // Pairs with HOOKMYAPP_CLOUDFLARED_BIN to make the test fully hermetic.
  // Not advertised in --help.
  const fakeTunnel = process.env.HOOKMYAPP_E2E_FAKE_TUNNEL === '1';
  const env = resolveEnv();
  let tunnel: TunnelStartResponse;
  if (fakeTunnel) {
    tunnel = {
      cloudflareTunnelToken: 'fake-e2e-token',
      hostname: 'fake-e2e.hookmyapp-sandbox.com',
      webhookPath: '/webhook',
    };
  } else {
    try {
      tunnel = (await apiClient(
        `/sandbox/sessions/${session.id}/tunnel/start`,
        {
          method: 'POST',
          body: JSON.stringify({ env }),
          workspaceId: session.workspaceId,
        },
      )) as TunnelStartResponse;
    } catch (err) {
      if (err instanceof ConflictError || err instanceof AuthError) {
        throw err;
      }
      const msg = err instanceof Error ? err.message : String(err);
      console.error(`Tunnel provisioning failed: ${msg}`);
      process.exit(3);
    }
  }

  // Step 7 — bind local proxy; configure ingress; spawn cloudflared.
  const proxy = await startProxyServer({
    upstreamPort: listenOpts.port,
    upstreamPath: listenOpts.path,
    onRequest: (line) => printLogLine(line, listenOpts),
  });

  if (!fakeTunnel) {
    try {
      await apiClient(
        `/sandbox/sessions/${session.id}/tunnel/configure`,
        {
          method: 'POST',
          body: JSON.stringify({ originPort: proxy.port }),
          workspaceId: session.workspaceId,
        },
      );
    } catch (err) {
      await proxy.close();
      const msg = err instanceof Error ? err.message : String(err);
      console.error(`Tunnel configure failed: ${msg}`);
      process.exit(3);
    }
  }

  const child = spawnCloudflared({
    binaryPath,
    token: tunnel.cloudflareTunnelToken,
  });

  // Step 8 — ready banner.
  printBanner({
    hostname: tunnel.hostname,
    localPort: listenOpts.port,
    path: listenOpts.path,
    session,
    json: listenOpts.json,
  });

  // Emit cli_sandbox_listen_started once the tunnel is live + start the
  // 2h PostHog liveness ping (backstop for unclean exits). The clean-exit
  // path below emits cli_sandbox_listen_stopped with duration_seconds.
  // All emits are fire-and-forget; fail-open in posthog.ts ensures
  // observability blips never break the listen command.
  // workspaceId is required for all downstream calls; callers guarantee it is
  // set (sessionWithWorkspace in the action handler coalesces it before passing
  // to this function). The ?? '' is a compile-time guard for the optional field
  // on SandboxSessionBase; at runtime workspaceId is always a real string here.
  const effectiveWorkspaceId = session.workspaceId ?? '';
  const listenStartedAt = Date.now();
  void emit('cli_sandbox_listen_started', {
    cli_version: getCliVersion(),
    session_public_id: session.id,
    workspace_public_id: effectiveWorkspaceId,
  });
  const posthogLiveness = startPosthogLiveness({
    sessionId: session.id,
    workspaceId: effectiveWorkspaceId,
  });

  // Step 9 — tunnel heartbeat loop (pings backend /tunnel/heartbeat every 30s
  // so the backend doesn't reap the session; separate from PostHog heartbeat
  // which is pure analytics every 5 minutes — CONTEXT.md §5 decision).
  const hb = startHeartbeat({
    sessionId: session.id,
    workspaceId: effectiveWorkspaceId,
    intervalMs: 30_000,
    onError: (err) => {
      process.stderr.write(
        `heartbeat: repeated failures (${err.message}); tunnel may be reconciled\n`,
      );
    },
  });

  // Step 11 — block until shutdown signal. Three resolution paths:
  //   (a) SIGINT  → graceful shutdown, return normally → caller exits 0
  //   (b) SIGTERM → graceful shutdown, return normally → caller exits 0
  //   (c) cloudflared child exits unexpectedly → graceful shutdown, exit 7
  // Re-entrancy: gracefulShutdown runs EXACTLY ONCE no matter how many
  // signals or exit events arrive (the `shuttingDown` flag below + the
  // flag inside lifecycle.ts work together).
  //
  // This Promise is the ONLY thing keeping the parent process alive after
  // setup — without it, src/index.ts:219 (`await flushAndExit(0)`) would
  // run immediately and the listen command would exit. RESEARCH §Pitfall 6
  // is now subsumed by case (c): the cloudflared-exit handler triggers the
  // same gracefulShutdown that stops the PostHog heartbeat.
  let shuttingDown = false;
  const runShutdown = async (): Promise<void> => {
    if (shuttingDown) return;
    shuttingDown = true;
    if (human) {
      process.stderr.write('\nShutting down…\n');
    }
    await gracefulShutdown({
      cloudflaredChild: child,
      proxyClose: () => proxy.close(),
      stopHeartbeat: hb.stop,
      stopPosthogLiveness: posthogLiveness.stop,
      callBackendStop: () =>
        fakeTunnel
          ? Promise.resolve()
          : apiClient(
              `/sandbox/sessions/${session.id}/tunnel/stop`,
              { method: 'POST', workspaceId: session.workspaceId },
            ).then(() => undefined).catch(() => undefined),
    });
  };

  await new Promise<void>((resolve) => {
    const onSignal = (): void => {
      // Clean exit — emit cli_sandbox_listen_stopped with the full session
      // duration. Cloudflared-died (onChildExit) does NOT emit this; the 2h
      // liveness ping is the duration backstop for unclean exits.
      void emit('cli_sandbox_listen_stopped', {
        cli_version: getCliVersion(),
        session_public_id: session.id,
        duration_seconds: Math.floor((Date.now() - listenStartedAt) / 1000),
      });
      void runShutdown().then(() => resolve());
    };
    const onChildExit = (
      code: number | null,
      signal: NodeJS.Signals | null,
    ): void => {
      // Pre-shutdown exit = unexpected. Log, run cleanup, exit non-zero (7).
      // We can't `resolve()` and let the caller exit 0 because the contract
      // is "tunnel died → parent dies non-zero".
      if (shuttingDown) {
        // We initiated the kill via gracefulShutdown — let runShutdown
        // resolve the Promise via its onSignal path.
        return;
      }
      process.stderr.write(
        `cloudflared exited unexpectedly (code=${code ?? 'null'}` +
          `${signal ? `, signal=${signal}` : ''}); shutting down listen.\n`,
      );
      void runShutdown().then(() => {
        process.exit(7);
      });
    };

    process.on('SIGINT', onSignal);
    process.on('SIGTERM', onSignal);
    child.once('exit', onChildExit);
  });
}

export function registerListenCommand(sandbox: Command, program: Command): void {
  const listen = sandbox
    .command('listen')
    .description(
      'Start a sandbox tunnel and stream incoming webhooks to your local app',
    )
    .argument(
      '[identifier]',
      'Positional shape-detected: phone | @username | ssn_XXXXXXXX',
    )
    .option(
      '--port <n>',
      'Local port your app listens on',
      parsePortArg,
      3000,
    )
    .option('--path <p>', 'Webhook path on your app', '/webhook')
    .option('--phone <phone>', 'Select WhatsApp session by phone')
    .option('--username <handle>', 'Select Instagram session by @handle')
    .option('--session <id>', 'Select any session by id (ssn_XXXXXXXX)')
    .option('--verbose', 'Print full request/response bodies', false)
    .option('--json', 'Machine-readable event log', false)
    .option(
      '--reinstall-tunnel-binary',
      'Force re-download of cloudflared',
      false,
    )
    .action(async (identifier: string | undefined, opts: ListenOpts) => {
      const human = !program.opts().json && !opts.json;

      // Step 0 — validate --session flag shape. Phase 117: must be a
      // ssn_<8-char> publicId; raw UUIDs are rejected here rather than at
      // the backend so CI scripts see exit 2 with the typed CliError code
      // instead of a 400 round-trip.
      if (opts.session && !isValidPublicId(opts.session, 'ssn')) {
        throw new ValidationError(
          `--session "${opts.session}" must be a publicId (ssn_<8-char>). Run: ${cliCommandPrefix()} sandbox status`,
        );
      }

      // Step 1 — auth gate. Throw AuthError so main() maps it to exit 4
      // (auth-required) rather than a blanket exit 1 that masks the real
      // condition from CI scripts.
      if (!(await readCredentials())) {
        throw new AuthError(`Not logged in. Run: ${cliCommandPrefix()} login`);
      }

      // Step 2 — version nudge (non-blocking, silent-on-error per 09a).
      await checkForNewerCli();

      // Step 4 — fetch active sessions. apiClient's own mapApiError already
      // produces typed subclasses (AuthError/NetworkError/ConflictError/...)
      // with the correct exit codes — let them propagate to main().
      const workspaceId = await getDefaultWorkspaceId();
      const dto = await apiClient('/sandbox/sessions?active=true', {
        method: 'GET',
        workspaceId,
      });
      const sessions = parseSandboxSessions(dto);

      // Step 5 — pick. pickSession throws CliError (exit 2) for
      // NO_ACTIVE_SESSIONS / SESSION_MISMATCH — let main() format + exit.
      const chosen = await pickSession({
        sessions,
        identifierArg: identifier,
        phoneFlag: opts.phone,
        usernameFlag: opts.username,
        sessionFlag: opts.session,
        isHuman: human,
      });

      // Ensure the session carries the correct workspaceId (listen picker
      // sessions come from a workspace-scoped list, but the type allows null).
      const sessionWithWorkspace: Session = {
        ...chosen,
        workspaceId: chosen.workspaceId ?? workspaceId,
      };

      // Steps 3 + 6-11: cloudflared binary, tunnel provision, local proxy,
      // banner, heartbeat, signal handlers. Shared with the post-login wizard.
      await runSandboxListenFlow(sessionWithWorkspace, opts);
    });

  addExamples(
    listen,
    `
EXAMPLES:
  $ hookmyapp sandbox listen
  $ hookmyapp sandbox listen --phone 15551234567 --port 3000
  $ hookmyapp sandbox listen --username @ordvir
  $ hookmyapp sandbox listen --path /webhook --verbose
  $ hookmyapp sandbox listen --session ssn_POWomFvq
  $ nohup hookmyapp sandbox listen --port 3000 &     # background / CI
`,
  );
}

function printLogLine(line: LogLine, opts: ListenOpts): void {
  if (opts.json) {
    process.stdout.write(JSON.stringify(line) + '\n');
    return;
  }
  process.stdout.write(
    `${line.ts}  ${line.method} ${line.path}  →  ${line.status}  (${line.ms}ms)   ${line.summary}\n`,
  );
}

export function printBanner(args: {
  hostname: string;
  localPort: number;
  path: string;
  session: Session;
  json: boolean;
}): void {
  if (args.json) return;
  const workspace = args.session.workspaceName ?? args.session.workspaceId;
  process.stdout.write(`\n✓ Tunnel active:    https://${args.hostname}\n`);
  process.stdout.write(
    `✓ Forwarding to:    http://localhost:${args.localPort}${args.path}\n`,
  );
  process.stdout.write(
    `📋 Logs UI:         http://localhost:${args.localPort}/logs\n`,
  );
  process.stdout.write(
    `  Test ${args.session.type === 'whatsapp' ? 'phone' : 'account'}: ${sessionIdentifier(args.session)} · Workspace: ${workspace}\n`,
  );
  process.stdout.write(`  Press Ctrl-C to stop.\n\n`);
}
